import * as S from './styles';
import Product from './ProductCard';
import { memo, useRef, useState, useEffect } from 'react';
import { CardMode, PackagesTypesProps, PriceProductProps, ProductProps, TaxSubstitutionProps } from '@/types';

const FALLBACK_CARD_HEIGHT: Record<CardMode, number> = {
  grid: 380,
  list: 190,
};
const lastMeasuredHeightByDirection: Partial<Record<CardMode, number>> = {};

export interface Props {
  product: ProductProps;
  direction?: CardMode;
  substTrib?: TaxSubstitutionProps[];
  handleModalFlex?: (product: ProductProps, selectedPack: PackagesTypesProps, selectedPackQty: number) => void;
  showFlex?: boolean;
  showInfos?: boolean;
  onProductNavigate?: () => void;
}

const ProductContent = ({
  product,
  substTrib,
  direction = 'grid',
  handleModalFlex,
  showFlex = true,
  showInfos = true,
  onProductNavigate,
}: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [elementHeight, setElementHeight] = useState<number | null>(
    () => lastMeasuredHeightByDirection[direction] ?? null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const productRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      {
        threshold: 0,
        rootMargin: '150px',
      }
    );

    const node = containerRef.current;
    if (node) observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const node = productRef.current;
    if (!node) return;

    const update = (height: number) => {
      const rounded = Math.round(height);
      if (rounded <= 0) return;
      lastMeasuredHeightByDirection[direction] = rounded;
      setElementHeight((prev) => (prev === rounded ? prev : rounded));
    };

    update(node.offsetHeight);

    if (typeof ResizeObserver === 'undefined') return;
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect?.height;
      if (typeof height === 'number') update(height);
    });
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, [isVisible, direction]);

  useEffect(() => {
    setElementHeight(lastMeasuredHeightByDirection[direction] ?? null);
  }, [direction]);

  const reservedHeight =
    elementHeight ?? lastMeasuredHeightByDirection[direction] ?? FALLBACK_CARD_HEIGHT[direction] ?? FALLBACK_CARD_HEIGHT.grid;

  return (
    <S.ProductContainer
      ref={containerRef}
      id={`product-${product.COD_PRODUTO}`}
      data-product-id={String(product.COD_PRODUTO)}
    >
      {isVisible ? (
        <div style={{ height: '100%' }} ref={productRef}>
          <Product
            key={`${product.COD_PRODUTO}`}
            direction={direction}
            substTrib={substTrib}
            product={product}
            handleModalFlex={handleModalFlex}
            showFlex={showFlex}
            showInfos={showInfos}
            onProductNavigate={onProductNavigate}
          />
        </div>
      ) : (
        <div style={{ height: reservedHeight, width: '100%' }} />
      )}
    </S.ProductContainer>
  );
};

export default memo(ProductContent);
